import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tag, Button, Collapse, message } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { reprocessNote, acceptNoteTags, getSimilarNotes } from '../services/api';
import type { SimilarItem } from '@compendium/shared';

interface NoteMetadataPanelProps {
  noteId: number;
  autoTagsRaw?: string;
  autoSummary?: string;
}

function parseTags(raw?: string): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((t): t is string => typeof t === 'string') : [];
  } catch {
    return [];
  }
}

export default function NoteMetadataPanel({ noteId, autoTagsRaw, autoSummary }: NoteMetadataPanelProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const originalTags = useMemo(() => parseTags(autoTagsRaw), [autoTagsRaw]);
  const [tags, setTags] = useState<string[]>(originalTags);
  const [similarExpanded, setSimilarExpanded] = useState(false);

  // Sync local tags when autoTagsRaw changes from outside (e.g. after reprocess)
  useEffect(() => {
    setTags(parseTags(autoTagsRaw));
  }, [autoTagsRaw]);

  const tagsModified = useMemo(() => {
    if (tags.length !== originalTags.length) return true;
    return tags.some((t, i) => t !== originalTags[i]);
  }, [tags, originalTags]);

  // Lazy-load similar items only when panel is expanded
  const { data: similar, isLoading: similarLoading } = useQuery({
    queryKey: ['note', noteId, 'similar'],
    queryFn: () => getSimilarNotes(noteId),
    enabled: similarExpanded,
  });

  const reprocessMutation = useMutation({
    mutationFn: () => reprocessNote(noteId),
    onSuccess: () => {
      message.info('Processing started — metadata will update shortly');
      queryClient.invalidateQueries({ queryKey: ['note', noteId] });
    },
    onError: () => {
      message.error('Failed to reprocess note');
    },
  });

  const acceptMutation = useMutation({
    mutationFn: (acceptedTags: string[]) => acceptNoteTags(noteId, acceptedTags),
    onSuccess: () => {
      message.success('Tags accepted');
      queryClient.invalidateQueries({ queryKey: ['note', noteId] });
    },
    onError: () => {
      message.error('Failed to accept tags');
    },
  });

  function handleRemoveTag(removedTag: string) {
    setTags((prev) => prev.filter((t) => t !== removedTag));
  }

  const hasMetadata = originalTags.length > 0 || !!autoSummary;

  // Always render the reprocess button; hide metadata sections if no data
  return (
    <div style={{ borderTop: '1px solid #222', paddingTop: 16, marginTop: 24 }}>
      {/* Header with reprocess button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: hasMetadata ? 12 : 0 }}>
        {hasMetadata && (
          <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888' }}>
            AI Metadata
          </span>
        )}
        {!hasMetadata && <span />}
        <Button
          size="small"
          type="text"
          icon={<ThunderboltOutlined />}
          loading={reprocessMutation.isPending}
          onClick={() => reprocessMutation.mutate()}
          style={{ color: '#888', fontSize: 12 }}
        >
          Reprocess with AI
        </Button>
      </div>

      {/* Auto-Tags */}
      {tags.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#666', marginBottom: 6 }}>
            Suggested Tags
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {tags.map((tag) => (
              <Tag
                key={tag}
                closable
                onClose={(e) => {
                  e.preventDefault();
                  handleRemoveTag(tag);
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid #222222',
                  color: '#d4af37',
                  borderRadius: 4,
                }}
              >
                {tag}
              </Tag>
            ))}
            {tagsModified && (
              <Button
                size="small"
                type="link"
                loading={acceptMutation.isPending}
                onClick={() => acceptMutation.mutate(tags)}
                style={{ color: '#d4af37', fontSize: 12, padding: '0 4px' }}
              >
                Accept Tags
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Auto-Summary */}
      {autoSummary && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#666', marginBottom: 4 }}>
            AI Summary
          </div>
          <div style={{ color: '#888', fontSize: 13, fontStyle: 'italic' }}>
            {autoSummary}
          </div>
        </div>
      )}

      {/* Similar Notes & Essays */}
      {hasMetadata && (
        <Collapse
          ghost
          size="small"
          onChange={(keys) => setSimilarExpanded(keys.length > 0)}
          items={[{
            key: 'similar',
            label: (
              <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#666' }}>
                Similar Notes & Essays
              </span>
            ),
            children: (
              <SimilarContent
                similar={similar}
                loading={similarLoading}
                onNavigateNote={(id) => navigate(`/reviewer/notes/${id}`)}
                onNavigateEssay={(slug) => navigate(`/reviewer/essays/${slug}`)}
              />
            ),
          }]}
        />
      )}
    </div>
  );
}

function SimilarContent({
  similar,
  loading,
  onNavigateNote,
  onNavigateEssay,
}: {
  similar?: { notes: SimilarItem[]; essays: SimilarItem[] };
  loading: boolean;
  onNavigateNote: (id: number) => void;
  onNavigateEssay: (slug: string) => void;
}) {
  if (loading) {
    return <div style={{ color: '#666', fontSize: 13, padding: '4px 0' }}>Loading...</div>;
  }

  const hasNotes = similar?.notes && similar.notes.length > 0;
  const hasEssays = similar?.essays && similar.essays.length > 0;

  if (!hasNotes && !hasEssays) {
    return <div style={{ color: '#666', fontSize: 13, padding: '4px 0' }}>No similar content found</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {similar?.notes.map((item) => (
        <div
          key={`note-${item.id}`}
          onClick={() => onNavigateNote(item.id)}
          style={{ cursor: 'pointer', padding: '4px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span style={{ color: '#f5f5f5', fontSize: 13 }}>{item.title}</span>
          {item.similarity != null && (
            <span style={{ color: '#666', fontSize: 11, marginLeft: 8, flexShrink: 0 }}>
              {Math.round(item.similarity * 100)}% similar
            </span>
          )}
        </div>
      ))}
      {hasNotes && hasEssays && (
        <div style={{ borderTop: '1px solid #222222', margin: '4px 0' }} />
      )}
      {similar?.essays.map((item) => (
        <div
          key={`essay-${item.id}`}
          onClick={() => onNavigateEssay(item.slug)}
          style={{ cursor: 'pointer', padding: '4px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span style={{ color: '#d4af37', fontSize: 13 }}>{item.title}</span>
          {item.similarity != null && (
            <span style={{ color: '#666', fontSize: 11, marginLeft: 8, flexShrink: 0 }}>
              {Math.round(item.similarity * 100)}% similar
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
